/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { useQuery } from '@apollo/client';
import { EnterpriseLearnerInsights_MetricTimeFrame as MetricTimeframe } from '__generated__/graphql-types';
import moment from 'moment-timezone';

import { useLocation, useNavigate } from 'js/lib/useRouter';
import {
  LONG_DATE_ONLY_NO_YEAR_DISPLAY,
  SHORT_MONTH_DAY_DISPLAY,
  formatDateTimeDisplay,
  momentWithUserTimezone,
} from 'js/utils/DateTimeUtils';

import { Grid, PageGridContainer, PageNotification, Typography2, breakpoints } from '@coursera/cds-core';

import CustomizeAnalyticsSettingsButton from 'bundles/enterprise-admin-home/components/CustomizeAnalyticsSettingsButton';
import DownloadUsageReportCard from 'bundles/enterprise-admin-home/components/DownloadUsageReportCard';
import HowLearnersAreLearningCard from 'bundles/enterprise-admin-home/components/HowLearnersAreLearningCard';
import LearnerActivityCard from 'bundles/enterprise-admin-home/components/LearnerActivityCard';
import TimePeriodSelector from 'bundles/enterprise-admin-home/components/TimePeriodSelector';
import TrendingSkillsCard from 'bundles/enterprise-admin-home/components/TrendingSkillsCard';
import EnterpriseLearnerInsights from 'bundles/enterprise-admin-home/queries/EnterpriseLearnerInsights.graphql';
import type {
  EnterpriseLearnerInsightsQuery as EnterpriseLearnerInsightsQueryData,
  EnterpriseLearnerInsightsQueryVariables,
} from 'bundles/enterprise-admin-home/queries/__generated__/EnterpriseLearnerInsights';
import { convertMetricTimeFrameToInt } from 'bundles/enterprise-admin-home/utils/LearningInsightsUtils';
import type { ProgramNameList } from 'bundles/enterprise-admin-home/utils/ProgramSummaryUtils';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import { useIntl } from 'bundles/page/lib/useIntl';

import _t from 'i18n!nls/enterprise-admin-home';

const styles = {
  wrapper: css`
    background-color: var(--cds-color-blue-50);
    width: 100%;
    padding-top: var(--cds-spacing-600);
    padding-bottom: var(--cds-spacing-400);
  `,
  headerBar: css`
    flex-direction: column;

    ${breakpoints.up('md')} {
      flex-direction: row;
      align-items: center;
    }
  `,
  chipGroup: css`
    justify-content: flex-end;

    ${breakpoints.down('sm')} {
      justify-content: flex-start;
    }
  `,
  header: css`
    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-300);
    }
  `,
  learnerDataSection: css`
    row-gap: var(--cds-spacing-300);
  `,
  skillsAndReport: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);
  `,
};

export type Props = {
  orgId: string;
  programNameList: ProgramNameList;
  orgSlug: string;
};

const LearningInsightsContainer = ({ orgId, orgSlug, programNameList }: Props) => {
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<MetricTimeframe>(MetricTimeframe.NinetyDays);
  const {
    loading,
    data = {},
    refetch,
    error,
  } = useQuery<EnterpriseLearnerInsightsQueryData, EnterpriseLearnerInsightsQueryVariables>(EnterpriseLearnerInsights, {
    variables: { orgId, metricsTimeFrame: selectedTimeFrame },
    context: { clientName: 'gatewayGql' },
  });
  const { query } = useLocation();
  const navigate = useNavigate();
  const { refetchAnalytics } = query;

  React.useEffect(() => {
    if (refetchAnalytics) {
      refetch({ orgId, metricsTimeFrame: selectedTimeFrame });
      navigate(location.pathname);
    }
  }, [refetchAnalytics, orgId, selectedTimeFrame, refetch, navigate]);

  const onSelectChipStub = (newTimeFrame: MetricTimeframe) => {
    setSelectedTimeFrame(newTimeFrame);
    refetch({ orgId, metricsTimeFrame: newTimeFrame });
  };

  const {
    numOfLearnersMakingProgress = 0,
    learnersMakingProgressChangePercentage = 0,
    numOfLearningHours = 0,
    numOfLearningHoursChangePercentage = 0,
    numOfVideosWatched = 0,
    numOfVideosWatchedChangePercentage = 0,
    numOfReadingsCompleted = 0,
    numOfReadingsCompletedChangePercentage = 0,
    numOfAssessmentsTaken = 0,
    numOfAssessmentsTakenChangePercentage = 0,
    trendingSkills = [],
    firstLearningInsightsDate,
    numOfEnrollments = 0,
    numOfEnrollmentsChangePercentage = 0,
  } = data?.EnterpriseLearnerInsights?.queryLearnerInsightsByOrg ?? {};

  const intl = useIntl();
  const todaysDate = momentWithUserTimezone();
  const validNumOfDays = firstLearningInsightsDate
    ? moment.duration(todaysDate.diff(moment(firstLearningInsightsDate))).asDays()
    : undefined;
  const daysToSubtract = convertMetricTimeFrameToInt(selectedTimeFrame);
  const timePeriodBeginDate = momentWithUserTimezone().subtract(daysToSubtract, 'days');
  const selectedTimePeriodLabel = `${formatDateTimeDisplay(
    timePeriodBeginDate,
    SHORT_MONTH_DAY_DISPLAY
  )} - ${formatDateTimeDisplay(todaysDate, SHORT_MONTH_DAY_DISPLAY)}`;
  return (
    <div css={styles.wrapper} data-testid="learning-insights-container">
      <PageGridContainer maxWidth="lg">
        <Grid item container xs={12} justifyContent="space-between" css={styles.headerBar}>
          <Typography2 component="h2" variant="titleSmall" color="body" css={styles.header}>
            {_t('Learning Insights')}

            <CustomizeAnalyticsSettingsButton />
          </Typography2>
          <TimePeriodSelector
            selectedTimeFrame={selectedTimeFrame}
            onSelectChipStub={onSelectChipStub}
            selectedTimePeriodLabel={selectedTimePeriodLabel}
          />
        </Grid>
        <React.Fragment>
          {validNumOfDays && validNumOfDays < convertMetricTimeFrameToInt(selectedTimeFrame) && (
            <Grid item xs={12}>
              <PageNotification severity="information">
                {intl.formatMessage(
                  _t(
                    'Your organisation is just getting started, so there isn’t enough data available to display learning insights for the entire selected time period; the data shown is limited to {firstDate} - {today}.'
                  ),
                  {
                    today: formatDateTimeDisplay(todaysDate, LONG_DATE_ONLY_NO_YEAR_DISPLAY),
                    firstDate: firstLearningInsightsDate
                      ? formatDateTimeDisplay(firstLearningInsightsDate, LONG_DATE_ONLY_NO_YEAR_DISPLAY)
                      : _t('your start date'),
                  }
                )}
              </PageNotification>
            </Grid>
          )}
        </React.Fragment>
        <Grid container item direction="row" spacing={24}>
          <Grid container item xs={12} lg={6} direction="column" css={styles.learnerDataSection}>
            <LearnerActivityCard
              numOfLearnersMakingProgress={numOfLearnersMakingProgress}
              numOfLearningHours={numOfLearningHours}
              numOfLearningHoursChangePercentage={numOfLearningHoursChangePercentage}
              learnersMakingProgressChangePercentage={learnersMakingProgressChangePercentage}
              timeframe={selectedTimeFrame}
              orgId={orgId}
              orgSlug={orgSlug}
              hasError={Boolean(error)}
              isLoading={loading}
              numOfEnrollments={numOfEnrollments as number}
              numOfEnrollmentsChangePercentage={numOfEnrollmentsChangePercentage as number}
            />
            <HowLearnersAreLearningCard
              numOfVideosWatched={numOfVideosWatched}
              numOfVideosWatchedChangePercentage={numOfVideosWatchedChangePercentage}
              numOfReadingsCompleted={numOfReadingsCompleted}
              numOfReadingsCompletedChangePercentage={numOfReadingsCompletedChangePercentage}
              numOfAssessmentsTaken={numOfAssessmentsTaken}
              numOfAssessmentsTakenChangePercentage={numOfAssessmentsTakenChangePercentage}
              timeframe={selectedTimeFrame}
              hasError={Boolean(error)}
              isLoading={loading}
            />
          </Grid>
          <Grid item xs={12} lg={6} css={styles.skillsAndReport}>
            <TrendingSkillsCard
              orgSlug={orgSlug}
              orgId={orgId}
              timeframe={selectedTimeFrame}
              trendingSkills={trendingSkills}
              hasError={Boolean(error)}
              isLoading={loading}
            />
          </Grid>
        </Grid>
        {/* We need to wrap the permissions in a Grid, because EnterprisePermissionsRequired wraps its children with a fragment
        and breaks the layout */}
        <Grid item xs={12}>
          <EnterprisePermissionsRequired domain="ANY_PROGRAM" subdomain="USAGE_REPORT" verb="EXPORT">
            <DownloadUsageReportCard orgId={orgId} thirdPartySlug={orgSlug} programNameList={programNameList} />
          </EnterprisePermissionsRequired>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default LearningInsightsContainer;
